import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import { getTableSchema } from './PartsTable/TableSchema';
import PartsTable from './PartsTable';
import { useBreakpoint, useSystemFeedback } from '@app/hooks';
import { ERROR_DOMAIN, ERROR_PATH } from '@app/constants/errorConstants';
import ExceptionFeedbackBySelector from '@app/components/common/Exception/ExceptionFeedbackBySelector';
import { IS_VALID_PARTS } from './constants';
import SISToCartModalActions from './SISToCartModalActions';
import { getCookie, isEmpty, removeCookie } from '@app/utils';
import { PAGE_BREAKPOINTS, STATUS } from '@app/constants/commonConstants';
import NoValidInvalidPartsMessage from './NoValidInvalidPartsMessage';
import {
  getDealerSOSDetails,
  setSISValueEmpty
} from '@app/store/checkout/actions';
import LoaderWithText from '@app/components/common/LoaderWithText';
import useSISToCartModal from './hooks/useSISToCartModal';
import { getParts } from '../ValidInvalidPartsModal/utils';
import { SIS_PARTS_LIST } from '@app/components/login/redirect/constants';
import styles from './validInvalidPartsTable.module.scss';

const ValidInvalidPartsModal = ({ modalOptions }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setError } = useSystemFeedback();
  const { open, close } = modalOptions;
  const isSM = useBreakpoint(PAGE_BREAKPOINTS.SM);
  const [errorPartsData, setErrorParts] = useState([]);
  const { filterFormikValues, deleteInvalidItem, validParts, inValidParts } =
    useSISToCartModal();
  const isInvalidParts = useSelector(state => state.checkout?.isInvalidParts);
  const sisImportValidAndInvalidParts = useSelector(
    state => state.checkout?.sisImportValidAndInvalidParts
  );
  const partItems = useSelector(state => state.checkout?.partItems);
  const addToCartSubmitStatus = useSelector(
    s => s.checkout.addToCartSubmitStatus
  );
  const sosItemDetailsStatus = useSelector(
    s => s.checkout?.sosItemDetailsStatus
  );
  const initialValues = {
    inValidItems: inValidParts || []
  };

  const dealerPriceAndAvailabilityStatus = useSelector(
    s => s.checkout.dealerPriceAndAvailabilityStatus
  );
  const addToCartSubmitPending = addToCartSubmitStatus === STATUS.PENDING;
  const sosItemDetalsPending = sosItemDetailsStatus === STATUS.PENDING;
  const dealerPriceAndAvailabilityPending =
    dealerPriceAndAvailabilityStatus === STATUS.PENDING;
  const showLoader =
    addToCartSubmitPending ||
    sosItemDetalsPending ||
    dealerPriceAndAvailabilityPending;
  const fireInvalidPartsHandler = errors => {
    if (errors && partItems) {
      const errorsPart = getParts(errors);
      setErrorParts(errorsPart);
      return errorPartsData;
    }
  };
  useEffect(() => {
    const sisCookie = getCookie(SIS_PARTS_LIST) || '';
    if (isInvalidParts && sisImportValidAndInvalidParts && sisCookie) {
      dispatch(
        setError(ERROR_DOMAIN.CART, ERROR_PATH.SIS_PARTS_ERROR, {
          title: 'INVALID_ITEM',
          message: 'INVALID_ITEMS2'
        })
      );
    } else if (!isInvalidParts) {
      close();
    }
  }, [
    close,
    dispatch,
    inValidParts,
    isInvalidParts,
    open,
    setError,
    sisImportValidAndInvalidParts
  ]);

  const onClose = val => {
    close();
    dispatch(setSISValueEmpty());
    val && removeCookie(SIS_PARTS_LIST);
  };

  const submit = async values => {
    const mergeValues = { ...values, inValidItems: inValidParts };
    const mergeItems = filterFormikValues(mergeValues);
    if (mergeItems.length > 0) {
      dispatch(
        getDealerSOSDetails({
          items: mergeItems,
          invalidPartsHandler: fireInvalidPartsHandler,
          sisFlow: true,
          t
        })
      ).then(errors => {
        if (isEmpty(errors)) {
          onClose();
        }
      });
    }
  };

  return (
    <div className={styles['validInvalidPartModal__scrollable-section']}>
      {showLoader && <LoaderWithText size={'small'} title="UPDATING" />}
      <Formik initialValues={initialValues} onSubmit={submit}>
        <Form className={`w-100 d-flex flex-column`} name="sis-conflict-modal">
          <p className="text-sans-serif u-fs--14 u-color--DarkGray mb-3">
            {t('SIS_TO_CART_MODAL2')}
          </p>
          <h3 className="h3 display-4">{t('INVALID_PARTS')}</h3>
          {!isEmpty(inValidParts) ? (
            <>
              <ExceptionFeedbackBySelector
                className="mb-1"
                selector={state =>
                  state.errors[ERROR_DOMAIN.CART]?.[ERROR_PATH.SIS_PARTS_ERROR]
                }
              />
              <PartsTable
                schema={getTableSchema(t, isSM)}
                parts={inValidParts}
                deleteInvalidItem={deleteInvalidItem}
              />
            </>
          ) : (
            <NoValidInvalidPartsMessage message={t('NO_INVALID_PARTS')} />
          )}
          <h3 className="h3 display-4 mt-3">{t('VALID_PARTS')}</h3>
          {!isEmpty(validParts) ? (
            <PartsTable
              schema={getTableSchema(t, isSM, IS_VALID_PARTS)}
              parts={validParts}
              isValidParts={IS_VALID_PARTS}
            />
          ) : (
            <NoValidInvalidPartsMessage message={t('NO_VALID_PARTS')} />
          )}
          <SISToCartModalActions close={onClose} />
        </Form>
      </Formik>
    </div>
  );
};
ValidInvalidPartsModal.propTypes = {
  modalOptions: PropTypes.shape({
    isOpen: PropTypes.bool,
    open: PropTypes.func,
    close: PropTypes.func
  })
};
export default ValidInvalidPartsModal;
